import { useAuthState } from '@auth/hooks';
import { CommunityDialog } from '@communities/community/components/CommunityDialog';
import type { Community } from '@communities/community/domain';
import { useCommunities } from '@communities/community/hooks';
import { CommunityGroupLoadingState, CommunityGroupHeader, CommunityGroupAuthRequired, CommunityGrid } from '@communities/community-groups/components';
import { useCommunityGroupDetail, useDeleteCommunityGroup } from '@communities/community-groups/hooks';
import { EntityType } from '@communities/content-management/domain';
import { TabNavigation } from '@communities/content-management/pages';
import { routes } from '@communities/routes';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { BackButton, ErrorDisplay, DeleteDialog, HeroBackground, SectionHeader } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { formatDate } from '@core/utils';
import { Container, Section } from '@soteria/design-system-ve';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityGroupDetailPage.css.ts';

/**
 * Community Group Detail Page Component
 *
 * Displays detailed information about a community group and its communities
 */

// eslint-disable-next-line complexity
function CommunityGroupDetailPageComponent() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const { data: communityGroup, isCommunityGroupAdmin, loading, errors } = useCommunityGroupDetail();
  const { data: communities, loading: _communitiesLoading, errors: _communitiesErrors } = useCommunities();
  const { deleteCommunityGroup, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunityGroup();

  // Feature flag for community group objects
  const isCommunityGroupObjectsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_GROUP_OBJECTS);

  // Dialog states
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isAddCommunityDialogOpen, setIsAddCommunityDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const communitiesCount = communities?.length || 0;

  const handleSelectCommunity = (community: Community) => {
    navigate(routes.communities.detail(community.id));
  };

  const handleDeleteSuccess = () => {
    setIsDeleteDialogOpen(false);
    navigate(routes.communityGroups.root);
  };

  const handleDeleteConfirm = async () => {
    if (!communityGroup) return;

    resetErrors();
    const success = await deleteCommunityGroup(communityGroup.id);

    if (success) {
      handleDeleteSuccess();
    }
  };

  // Authentication check
  if (!isAuthenticated) {
    return <CommunityGroupAuthRequired />;
  }

  if (loading) {
    return <CommunityGroupLoadingState />;
  }

  // If there are errors but no community group data, show loading state
  // The error dialog will handle the error display
  if (!communityGroup) {
    return <CommunityGroupLoadingState />;
  }

  return (
    <>
      <HeroBackground imageUrl="/hero/hero-7.png" />
      <div>
        <div className={styles.headerSectionWithGradient}>
          <Container padding="xxlarge" size="xxl">
            {/* Back Button Section */}
            <Section className={styles.groupDetailPageHeadingStyle} spacing="small">
              <div className={styles.backButtonContainer}>
                <BackButton onClick={() => navigate(routes.communityGroups.root)} />
              </div>
            </Section>

            <CommunityGroupHeader
              communitiesCount={communitiesCount}
              createdAt={formatDate(communityGroup.createdAt) || ''}
              createdBy={communityGroup?.createdBy || 'Unknown'}
              description={communityGroup?.description || ''}
              isCommunityGroupAdmin={isCommunityGroupAdmin}
              lastModifiedAt={formatDate(communityGroup.lastModifiedAt) || ''}
              name={communityGroup?.name || ''}
              onAddCommunity={() => setIsAddCommunityDialogOpen(true)}
              onDeleteGroup={() => setIsDeleteDialogOpen(true)}
            />

            <Section className={styles.communitiesSection}>
              <SectionHeader description="Communities in this group." title="Communities" />
              <div className={styles.communityGridStyle}>
                <CommunityGrid
                  columnFit="auto-fill"
                  communities={communities}
                  emptyStateClassName={styles.emptyCommunitiesStateStyle}
                  onSelectCommunity={handleSelectCommunity}
                />
              </div>
            </Section>
          </Container>

          {/* Gradient Overlay */}
          <div className={styles.gradientOverlay} />
        </div>

        {/* Content Section with Solid Background and Navigation */}
        {isCommunityGroupObjectsEnabled && (
          <div className={styles.contentSection}>
            {/* Tab Content with Navigation */}
            <div className={styles.tabContent}>
              {communityGroup && <TabNavigation entityId={communityGroup.id} entityType={EntityType.COMMUNITY_GROUP} isEntityAdmin={isCommunityGroupAdmin} />}
            </div>
          </div>
        )}

        {/* Community Dialog */}
        {communityGroup !== null && (
          <CommunityDialog groupId={communityGroup.id} isOpen={isAddCommunityDialogOpen} onClose={() => setIsAddCommunityDialogOpen(false)} />
        )}

        {/* Delete Community Group Dialog */}
        {communityGroup !== null && (
          <DeleteDialog
            description="Are you sure you want to delete this community group?"
            errors={deleteErrors}
            isDeleting={isDeleting}
            isOpen={isDeleteDialogOpen}
            itemName={communityGroup.name}
            itemType="Community Group"
            title="Delete Community Group"
            onClose={() => {
              setIsDeleteDialogOpen(false);
              resetErrors();
            }}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </div>

      {/* Error Display - Strategy-based rendering */}
      {errors && <ErrorDisplay isOpen={showErrorDialog} message={formatErrorMessages(errors)} title="Error" onClose={() => setShowErrorDialog(false)} />}
    </>
  );
}

export const CommunityGroupDetailPage = withAppInsightsTracking(CommunityGroupDetailPageComponent, 'CommunityGroupDetailPage');
